import { FabricObject } from 'fabric';
import { Clip, Track } from '../types';

/**
 * Shared association logic between video clips and the text/subtitle clips that "belong" to
 * them (a scene). Used by TrackArea (row grouping + drag), LayerColumn (scene groups + expand
 * on select) and TimelinePanel (cascading delete/duplicate) — kept in one place so a fix applies
 * everywhere at once instead of drifting between copies.
 *
 * The video<->subtitle link is read directly from `Clip.timeAttach` (mirrors the persisted
 * `Layer.timeAttach`, see model/types.ts) — an explicit, directed edge (subtitle -> its video)
 * stamped at creation time, never guessed from time overlap or read off the Fabric object. See
 * docs/subtitulos-link-en-el-modelo.md for why the old heuristic was replaced.
 */

export type TimelineClipEntry = {
  track: Track;
  clip: Clip | undefined;
  fabricObject: FabricObject | undefined;
};

export type TimedClipEntry = TimelineClipEntry & { clip: Clip };

export type AttachmentGraph = Map<string, Set<string>>;

const addEdge = (graph: AttachmentGraph, a: string, b: string): void => {
  if (a === b) return;
  if (!graph.has(a)) graph.set(a, new Set());
  if (!graph.has(b)) graph.set(b, new Set());
  graph.get(a)?.add(b);
  graph.get(b)?.add(a);
};

/** Builds a non-directed graph (for co-movement/cascade) from each clip's own `timeAttach` link. */
export const buildAttachmentGraph = (entries: TimelineClipEntry[]): AttachmentGraph => {
  const graph: AttachmentGraph = new Map();
  for (const entry of entries) {
    const videoId = entry.clip?.timeAttach?.videoId;
    if (videoId) addEdge(graph, entry.clip!.id, videoId);
  }
  return graph;
};

export const getConnectedClipIds = (graph: AttachmentGraph, startClipId: string): Set<string> => {
  const visited = new Set<string>();
  const stack = [startClipId];

  while (stack.length > 0) {
    const clipId = stack.pop();
    if (!clipId || visited.has(clipId)) continue;
    visited.add(clipId);
    const neighbors = graph.get(clipId);
    if (!neighbors) continue;
    for (const neighbor of Array.from(neighbors)) {
      if (!visited.has(neighbor)) stack.push(neighbor);
    }
  }

  return visited;
};

/** For every text clip, the id of the video clip it's linked to (if any) — a direct read, not a graph walk. */
export const buildTextVideoMap = (entries: TimelineClipEntry[]): Map<string, string> => {
  const result = new Map<string, string>();
  for (const entry of entries) {
    if (entry.clip?.timeAttach?.videoId) result.set(entry.clip.id, entry.clip.timeAttach.videoId);
  }
  return result;
};
